import { AdTemplate } from '@/components/AdTemplate';
import { Layout } from '@/components/Layout';
import { MorePagesTitle } from '@/components/MorePagesTitle';
import { PageMeta } from '@/components/PageMeta';
import { SchemaMarkup } from '@/components/SchemaMarkup';
import { useAppState } from '@/hooks/useAppState';
import { Meta } from '@/types/content';
import { CommonPageProps } from '@/types/page';
import { isDeviceInvalid } from '@/utils/isDeviceInvalid';
import { CLOUDFLARE_TTL } from '@/utils/ttl';
import { withCacheControlHeaders } from '@/utils/withCacheControlHeaders';
import { withLockedContentHeaders } from '@/utils/withLockedContentHeaders';
import { withOcelotCacheTagsHeaders } from '@/utils/withOcelotCacheTagsHeaders';
import { withSerializationGuard } from '@/utils/withSerializationGuard';
import { withTestContentHeaders } from '@/utils/withTestContentHeaders';
import { InfinityScrollArticle } from 'base/components/InfinityScrollArticle';
import { InfinityScrollFront } from 'base/components/InfinityScrollFront';
import { getOcelotConfig } from 'lib/configs/ocelot';
import { getRecommendedArticles } from 'lib/data/recommendations';
import { getLabradorPage } from 'lib/labrador/getLabradorPage';
import { getLabradorPageProps, LabradorPageProps } from 'lib/labrador/getLabradorPageProps';
import { handleCommonLabradorPageStatus } from 'lib/labrador/handleCommonLabradorPageStatus';
import { stripSlashes } from 'lib/labrador/utils';
import { getPaymentConfig } from 'lib/services/payments';
import { renderPostTypeComponent } from 'modules/dynamic/components';
import { GetServerSideProps, NextPage } from 'next';
import { useEffectOnce } from 'react-use';

const Page: NextPage<LabradorPageProps & CommonPageProps> = ({ pageData, adsData, analyticsData }) => {
  const [{ activeInfinityScrollArticle }, appStateDispatcher] = useAppState();

  const { infinityScrollFront, infinityScrollArticle, recommendedArticles } = getOcelotConfig('features');

  const infinityScrollId = pageData.meta.infinityScrollID;
  const infinityScrollMode = pageData.meta.infinityScrollMode as 'front' | 'article';
  const category = pageData.data.category;
  const { accessLevel } = pageData.meta;
  const { explicitRoute } = infinityScrollFront;
  const { route: explicitFrontRoute } = explicitRoute?.find((route) => route.accessLevel === accessLevel) || {};

  const showInfinityScrollArticle =
    infinityScrollArticle.enabled && !infinityScrollArticle.excludedAccessLevels.includes(pageData.meta.accessLevel);

  const showInfinityScrollFront =
    infinityScrollFront.enabled &&
    !activeInfinityScrollArticle &&
    !infinityScrollFront.excludedAccessLevels.includes(pageData.meta.accessLevel);

  const showExplicitFrontRoute = infinityScrollFront.enabled && !activeInfinityScrollArticle && explicitFrontRoute;
  useEffectOnce(() => {
    (async () => {
      if (!recommendedArticles.enabled && !showInfinityScrollArticle) return;

      const { recommended_articles, scroll_articles } = await getRecommendedArticles();
      appStateDispatcher({
        recommendedArticles: recommended_articles || [],
        scrollArticles: scroll_articles || [],
      });
    })();
  });

  const getSourceRoute = () => {
    if (explicitFrontRoute) return explicitFrontRoute;

    if (infinityScrollMode === 'front') {
      return infinityScrollId
        ? ['_scroll', 'front', infinityScrollId].join('/')
        : ['_scroll', 'article', category].join('/');
    }
    return ['_scroll', 'article', category].join('/');
  };

  return (
    <Layout
      content={
        <>
          <PageMeta pageData={pageData} />
          <AdTemplate pageMeta={pageData?.meta as Meta} adsData={adsData} />
          <SchemaMarkup pageData={pageData} />
          {renderPostTypeComponent(pageData)}
          {showInfinityScrollArticle && <InfinityScrollArticle pageData={pageData} />}
          {(showInfinityScrollFront || showExplicitFrontRoute) && (
            <InfinityScrollFront
              headline={<MorePagesTitle content={infinityScrollFront.title} />}
              pageData={pageData}
              sourceRoute={getSourceRoute()}
            />
          )}
        </>
      }
    />
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { device, params = [] } = context.params as { device: string; params?: string[] };

  if (isDeviceInvalid(device)) {
    return { notFound: true };
  }

  const routeKey = params;
  const data = await getLabradorPage(device, routeKey, context);

  const commonLabradorResponse = handleCommonLabradorPageStatus(data);
  if (commonLabradorResponse) return commonLabradorResponse;

  const publishedUrl = data?.pageData?.data?.publishedUrl;
  if (stripSlashes(publishedUrl) !== routeKey.join('/')) {
    return { redirect: { destination: publishedUrl, permanent: true } };
  }

  withOcelotCacheTagsHeaders(context, data);
  withLockedContentHeaders(context, data);
  withTestContentHeaders(context, data);
  withCacheControlHeaders(context.res, CLOUDFLARE_TTL);

  const isPaid = data.pageData.meta.accessLevel === 'purchase';

  return withSerializationGuard({
    props: {
      ...getLabradorPageProps(data),
      paymentConfig: isPaid ? await getPaymentConfig() : undefined,
    },
  });
};

export default Page;
